import { useDrag } from '@use-gesture/react';
import { clamp } from 'lodash';
import { type FC, useEffect, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import ConnectedSideBar from './SideBar/ConnectedSideBar';

export const SIDEBAR_WIDTH_BREAKPOINT = 750;

interface Props {
  closed: boolean;
  onClose: () => void;
}

export const PlayerSideBar: FC<Props> = ({ closed, onClose }) => {
  const [showContent, setShowContent] = useState(!closed);
  const windowWidth = window.innerWidth;
  const sidebarWidth =
    windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? clamp(windowWidth * 0.3, 300, 400) : windowWidth - 50;
  const [{ x }, api] = useSpring(() => ({
    x: closed ? -sidebarWidth : 0,
    onRest: (result) => {
      setShowContent(result.value.x > -sidebarWidth);
    },
  }));

  useEffect(() => {
    if (closed) {
      api.start({ x: -sidebarWidth });
    } else {
      api.start({ x: 0 });
      setShowContent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed]);

  const bind = useDrag(({ down, last, tap, movement: [mx], event }) => {
    event.preventDefault();
    if (tap) {
      onClose();
      return;
    }
    if (last) {
      if (mx < -sidebarWidth / 2) {
        onClose();
        return;
      }
    }
    api.start({ x: down ? Math.min(mx, 0) : 0, immediate: down });
  }, {});

  return (
    <>
      <animated.div
        className="side-bar-container"
        style={{
          width: windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? x.to((x) => sidebarWidth + x) : undefined,
          x: windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? undefined : x,
        }}
        onTransitionEnd={() => {
          setShowContent(!closed);
        }}
      >
        {showContent && (
          <div style={{ width: sidebarWidth, height: '100%' }}>
            <ConnectedSideBar onClose={onClose} />
          </div>
        )}
      </animated.div>
      <animated.div
        className="side-bar-backdrop"
        style={{
          backdropFilter: x.to(
            (x) =>
              `brightness(${x === -sidebarWidth ? 1 : Math.min(1, -0.7 * (x / sidebarWidth) + 0.3)})`,
          ),
          WebkitBackdropFilter: x.to(
            (x) =>
              `brightness(${x === -sidebarWidth ? 1 : Math.min(1, -0.7 * (x / sidebarWidth) + 0.3)})`,
          ),
        }}
        {...bind()}
      />
    </>
  );
};
